import {
  AddIcon,
  CloseIcon,
  EditIcon,
  RepeatIcon,
  SearchIcon,
} from '@chakra-ui/icons';
import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  LinkBox,
  Spinner,
  Switch,
  Text,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';

import { Link as ReactRouterLink, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import useToastNotif from 'hooks/useToastNotif';
import debounce from 'lodash.debounce';
import { useCallback, useMemo, useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { useDispatch, useSelector } from 'react-redux';
import { getClassesAction } from 'store/modules/class/class-action';
import classColumns from 'utils/columns/classColumns';
import { ReducerStatus } from 'utils/constants';
import { ADMIN, TRAINER } from 'utils/constants/roles';
import { TOAST_ERROR } from 'utils/constants/toast';
import { classActions } from 'store/modules/class/class-slice';
import UpdateClassMd from 'components/class/modals/UpdateClassMd';
import DeleteClassMd from 'components/class/modals/DeleteClassMd';
import { FaEye } from 'react-icons/fa';

export const ActionColumn = () => {
  const dispatch = useDispatch();

  const handleEditButton = (id) => {
    dispatch(classActions.setUpdateClass({ modal: true, id }));
  };

  const handleDeleteButton = (id) => {
    dispatch(classActions.setDeleteClass({ modal: true, id }));
  };

  return {
    name: 'Action',
    sortable: false,
    cell: (row) => (
      <Flex gap={2}>
        <Tooltip label="View" fontSize="md">
          <LinkBox as={ReactRouterLink} to={`/admin/classes/${row.id}`}>
            <Button colorScheme="green" size="xs">
              <FaEye />
            </Button>
          </LinkBox>
        </Tooltip>

        <Tooltip label="Edit" fontSize="md">
          <Button
            colorScheme="blue"
            size="xs"
            onClick={() => handleEditButton(row.id)}
          >
            <EditIcon />
          </Button>
        </Tooltip>
        <Tooltip label="Remove" fontSize="md">
          <Button
            colorScheme="gray"
            size="xs"
            onClick={() => handleDeleteButton(row.id)}
          >
            <CloseIcon />
          </Button>
        </Tooltip>
      </Flex>
    ),
  };
};

function ClassListAdminPage() {
  document.title = 'Skill Quest | Admin - Classes';

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { accounts } = useMsal();

  // Safe access to user roles with proper null/undefined checks
  const userAccount = accounts?.[0];
  const idTokenClaims = userAccount?.idTokenClaims;
  const roles = idTokenClaims?.roles || [];

  // Check if user has admin or trainer role for admin access
  const hasAdminOrTrainerRole = roles.some((role) =>
    [ADMIN, TRAINER].includes(role)
  );

  /**
   * Permission-based redirect for admin access control
   * If user doesn't have ADMIN or TRAINER role, redirect to non-admin classes list
   * Uses replace: true to prevent back navigation to unauthorized page
   */
  useEffect(() => {
    if (!hasAdminOrTrainerRole) {
      navigate('/classes', { replace: true });
    }
  }, [hasAdminOrTrainerRole, navigate]);

  const { generateToastNotif } = useToastNotif();

  const classes = useSelector((state) => state.class.classes);

  const classesMetaData = useSelector((state) => state.class.classesMetaData);

  const getClasses = useSelector((state) => state.class.getClasses);

  const [perPage, setPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('asc');
  const [sortBy, setSortBy] = useState('');
  const [searchText, setSearchText] = useState('');

  const fetchData = useCallback(
    ({
      selectedPage,
      selectedPageSize,
      selectedOrderBy,
      selectedSortBy,
      selectedQuery,
    }) => {
      dispatch(
        getClassesAction({
          currentPage: selectedPage || 1,
          pageSize: selectedPageSize || 10,
          orderBy: selectedOrderBy,
          sortBy: selectedSortBy,
          query: selectedQuery,
        })
      );
    },
    [dispatch]
  );

  const handlePerRowsChange = (newPerPage, page) => {
    if (newPerPage === perPage) return;
    setPerPage(newPerPage);
    fetchData({
      selectedPage: page,
      selectedPageSize: newPerPage,
      selectedOrderBy: orderBy,
      selectedSortBy: sortBy,
      selectedQuery: searchText,
    });
  };

  const handleSort = (column, sortDirection) => {
    if (!column?.sortField && sortDirection === orderBy) return;
    setOrderBy(sortDirection);
    setSortBy(column.sortField);
    fetchData({
      selectedPage: 1,
      selectedPageSize: perPage,
      selectedOrderBy: sortDirection,
      selectedSortBy: column.sortField,
      selectedQuery: searchText,
    });
  };

  const handlePageChange = (page) => {
    fetchData({
      selectedPage: page,
      selectedPageSize: perPage,
      selectedOrderBy: orderBy,
      selectedSortBy: sortBy,
      selectedQuery: searchText,
    });
  };

  const debouncedFetchData = useMemo(
    () =>
      debounce((query) => {
        fetchData({
          selectedPage: 1,
          selectedPageSize: perPage,
          selectedQuery: query,
        });
      }, 350),
    [fetchData, perPage]
  );

  // Update search text and trigger debounced fetch
  const handleSearchTextChange = (e) => {
    setSearchText(e.target.value);
    debouncedFetchData(e.target.value);
  };

  useEffect(() => {
    if (getClasses.status === ReducerStatus.FAILED) {
      generateToastNotif(
        TOAST_ERROR,
        getClasses.error?.data?.status || '500',
        getClasses.error?.data?.title ||
          'Something went wrong with the request',
        true,
        false
      );
    }
  }, [getClasses, generateToastNotif]);

  // Load data on component mount
  useEffect(() => {
    fetchData({});
  }, [fetchData]);

  return (
    <Box ml={{ base: 0, md: 20 }} minH="100vh" p="10">
      <UpdateClassMd />
      <DeleteClassMd />
      <Flex my={4} gap={2} align="center">
        <Text flex={1} fontSize="2xl">
          Classes
        </Text>

        <LinkBox as={ReactRouterLink} to="/admin/classes/create">
          <Button colorScheme="red" leftIcon={<AddIcon />}>
            Create
          </Button>
        </LinkBox>
      </Flex>

      <FormControl
        my={2}
        display="flex"
        alignItems="center"
        justifyContent="end"
      >
        <FormLabel htmlFor="email-alerts" fontSize="sm" mb="0">
          Edit Mode
        </FormLabel>
        <LinkBox as={ReactRouterLink} to="/classes">
          <Switch id="edit-mode-on" isChecked />
        </LinkBox>
      </FormControl>

      <Box w="100%" p={4} color="gray.500" borderWidth="1px" borderRadius="lg">
        <Flex m={2} justify={{ base: 'center', md: 'end' }}>
          <IconButton
            aria-label="Reload table"
            variant="link"
            onClick={fetchData}
            icon={<RepeatIcon />}
          />
          <InputGroup width={{ base: '100%', md: '350px' }}>
            <InputRightElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputRightElement>
            <Input
              name="query"
              placeholder="Search"
              value={searchText}
              onChange={handleSearchTextChange}
            />
          </InputGroup>
        </Flex>

        <Divider mt={4} />

        <DataTable
          theme={useColorModeValue('default', 'dark')}
          progressPending={getClasses.status === ReducerStatus.LOADING}
          columns={[...classColumns, ActionColumn()]}
          data={classes}
          paginationTotalRows={classesMetaData?.totalItems}
          onChangeRowsPerPage={handlePerRowsChange}
          onSort={handleSort}
          onChangePage={handlePageChange}
          pagination
          paginationServer
          sortServer
          progressComponent={
            <Center my={10}>
              <Spinner
                thickness="4px"
                speed="0.65s"
                emptyColor="gray.200"
                color="blue.500"
                size="xl"
              />
            </Center>
          }
        />
      </Box>
    </Box>
  );
}

export default ClassListAdminPage;
